import {memberRoleActsForTrustee} from '@sollapay/domain/rules';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {showResultToastCard} from '@/components';

import {useTrustAccountMemberMutations} from './useTrustAccountMemberMutations';
import {memberDisplayName} from '../utils';

import type {TeamMember} from '../types';
import type {TrustAccountMemberRole} from '@sollapay/enums';
import type {TrustAccountMemberUserDTO} from '@sollapay/types';

type Person = Pick<TrustAccountMemberUserDTO, 'userId' | 'name' | 'email'>;

/**
 * Add, move and remove members on a saved account, each confirmed with an undo toast.
 * Each undo is built from what the server reports it changed, and the server refuses it if the
 * member has changed again since — so an old toast can never overwrite newer access.
 */
export function useTeamMemberActions(trustAccountId: string) {
  const {t} = useTranslation('trustAccounts');
  const mutations = useTrustAccountMemberMutations();
  const [error, setError] = useState<unknown>(null);

  const showChangeToast = (description: string, onUndo: () => void) =>
    showResultToastCard({
      isError: false,
      title: t('members.toast.title'),
      description,
      closeLabel: t('members.toast.close'),
      action: {label: t('members.toast.undo'), onClick: onUndo}
    });

  // A toast outlives the Team tab, so an undo that fails after navigating away is still reported.
  const showUndoFailedToast = () =>
    showResultToastCard({
      isError: true,
      title: t('members.toast.undoFailedTitle'),
      description: t('members.toast.undoFailed'),
      closeLabel: t('members.toast.close')
    });

  /** @returns Whether the change went through; a failure is kept in `error` instead of thrown. */
  const run = async <Result>(
    action: () => Promise<Result>,
    message: string,
    toUndo: (result: Result) => () => Promise<unknown>
  ): Promise<boolean> => {
    setError(null);
    try {
      const undo = toUndo(await action());
      showChangeToast(message, () => void undo().catch(showUndoFailedToast));
      return true;
    } catch (failure) {
      setError(failure);
      return false;
    }
  };

  // A role acting in the trustee's name says so: the change came with their power of attorney.
  const changeMessage = (kind: 'added' | 'moved', person: Person, role: TrustAccountMemberRole) =>
    t(`members.toast.${kind}${memberRoleActsForTrustee(role) ? 'Operator' : ''}`, {
      name: memberDisplayName(person),
      role: t(`members.roles.${role}.one`)
    });

  /** `deferRefresh` skips refetching the team, for one of several adds that refresh once at the end. */
  const add = (
    person: Person,
    role: TrustAccountMemberRole,
    {deferRefresh = false}: {deferRefresh?: boolean} = {}
  ) =>
    run(
      () => mutations.add.mutateAsync({trustAccountId, userId: person.userId, role, deferRefresh}),
      changeMessage('added', person, role),
      ({data: added}) =>
        () =>
          mutations.remove.mutateAsync({
            trustAccountId,
            userId: person.userId,
            expectedVersion: added.version
          })
    );

  const move = (person: Person, to: TrustAccountMemberRole) =>
    run(
      () => mutations.changeRole.mutateAsync({trustAccountId, userId: person.userId, role: to}),
      changeMessage('moved', person, to),
      ({data: moved}) =>
        () =>
          mutations.changeRole.mutateAsync({
            trustAccountId,
            userId: person.userId,
            role: moved.previousRole,
            expectedVersion: moved.version
          })
    );

  const remove = (person: Person) =>
    run(
      () => mutations.remove.mutateAsync({trustAccountId, userId: person.userId}),
      t('members.toast.removed', {name: memberDisplayName(person)}),
      ({data: removed}) =>
        () =>
          mutations.add.mutateAsync({trustAccountId, userId: person.userId, role: removed.role})
    );

  const copyOne = async (existing: CopyMember | undefined, {userId, role}: CopyMember) => {
    if (!existing) {
      const {data: added} = await mutations.add.mutateAsync({trustAccountId, userId, role});
      return () =>
        mutations.remove.mutateAsync({trustAccountId, userId, expectedVersion: added.version});
    }
    const {data: moved} = await mutations.changeRole.mutateAsync({trustAccountId, userId, role});
    return () =>
      mutations.changeRole.mutateAsync({
        trustAccountId,
        userId,
        role: moved.previousRole,
        expectedVersion: moved.version
      });
  };

  // Sequential: each change bumps the member's version, and the server checks it on undo.
  const copy = async ({current, copied, trustName}: CopyTeamParams) => {
    const undos: Array<() => Promise<unknown>> = [];
    setError(null);
    try {
      for (const member of copied) {
        const existing = current.find(({userId}) => userId === member.userId);
        if (existing?.role !== member.role) undos.push(await copyOne(existing, member));
      }
    } catch (failure) {
      setError(failure);
    }
    if (undos.length === 0) return;
    // Every undo runs even if one is refused, so a member changed since can't strand the rest.
    const undoAll = async () => {
      let hasFailure = false;
      for (const undo of [...undos].reverse()) {
        try {
          await undo();
        } catch {
          hasFailure = true;
        }
      }
      if (hasFailure) showUndoFailedToast();
    };
    showChangeToast(t('members.copyTeam.copied', {name: trustName}), () => void undoAll());
  };

  return {add, move, remove, copy, error};
}

export type CopyMember = Pick<TeamMember, 'userId' | 'role'>;

export interface CopyTeamParams {
  current: CopyMember[];
  copied: CopyMember[];
  trustName: string;
}
